import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Radio, Save, Plus, Trash2, Shield, ShieldCheck, Eye, EyeOff, Copy, Check, ShieldAlert, Key } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { relayListService } from '../services/relayListService';
import { useToast } from '../contexts/ToastContext';
import { nip19 } from 'nostr-tools';

interface Relay {
    url: string;
    read: boolean;
    write: boolean;
}

export function SettingsPage() {
    const { pubkey, sk } = useAuth();
    const { showToast } = useToast();
    const [relays, setRelays] = useState<Relay[]>([]);
    const [newRelayUrl, setNewRelayUrl] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Safety & Backup State
    const [showSk, setShowSk] = useState(false);
    const [copied, setCopied] = useState(false);
    const nsec = sk ? nip19.nsecEncode(sk) : '';

    const handleCopy = () => {
        if (!nsec) return;
        navigator.clipboard.writeText(nsec);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        showToast('Gizli anahtar kopyalandı', 'success');
    };

    useEffect(() => {
        if (pubkey) {
            loadRelays();
        }
    }, [pubkey]);

    const loadRelays = async () => {
        if (!pubkey) return;

        setIsLoading(true);
        try {
            const userRelays = await relayListService.fetchRelayList(pubkey);
            setRelays(userRelays.length > 0 ? userRelays : getDefaultRelays());
        } catch (error) {
            console.error('Failed to load relays:', error);
            setRelays(getDefaultRelays());
        } finally {
            setIsLoading(false);
        }
    };

    const getDefaultRelays = (): Relay[] => [
        { url: 'wss://relay.damus.io', read: true, write: true },
        { url: 'wss://nos.lol', read: true, write: true },
        { url: 'wss://relay.snort.social', read: true, write: true },
        { url: 'wss://relay.primal.net', read: true, write: true },
    ];

    const addRelay = () => {
        if (!newRelayUrl.trim()) return;

        // Basic validation
        if (!newRelayUrl.startsWith('wss://') && !newRelayUrl.startsWith('ws://')) {
            showToast('Sunucu adresi wss:// veya ws:// ile başlamalı', 'error');
            return;
        }

        if (relays.some(r => r.url === newRelayUrl)) {
            showToast('Bu sunucu zaten listede', 'error');
            return;
        }

        setRelays([...relays, { url: newRelayUrl, read: true, write: true }]);
        setNewRelayUrl('');
    };

    const removeRelay = (url: string) => {
        setRelays(relays.filter(r => r.url !== url));
    };

    const toggleRelayRead = (url: string) => {
        setRelays(relays.map(r => r.url === url ? { ...r, read: !r.read } : r));
    };

    const toggleRelayWrite = (url: string) => {
        setRelays(relays.map(r => r.url === url ? { ...r, write: !r.write } : r));
    };

    const saveRelays = async () => {
        setIsSaving(true);
        try {
            await relayListService.publishRelayList(relays, sk);
            showToast('Sunucu listesi kaydedildi', 'success');
        } catch (error) {
            console.error('Failed to save relays:', error);
            showToast('Kaydetme başarısız oldu', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto space-y-6"
        >
            {/* Header */}
            <div className="bg-white dark:bg-surface-900 rounded-3xl p-8 border border-surface-200 dark:border-surface-700 shadow-xl">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-mavi-500 to-mavi-600 rounded-2xl flex items-center justify-center shadow-lg">
                        <Settings className="w-7 h-7 text-white" />
                    </div>
                    <div>
                        <h1 className="font-poppins font-bold text-3xl text-gray-900 dark:text-white">
                            Ayarlar
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-merriweather">
                            Erişim tercihlerini yönet
                        </p>
                    </div>
                </div>
            </div>

            {/* App Settings */}
            <div className="bg-white dark:bg-surface-900 rounded-2xl p-8 border border-surface-200 dark:border-surface-700 space-y-6">
                <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-mavi-500" />
                    <h2 className="font-poppins font-bold text-xl text-gray-900 dark:text-white">
                        Uygulama Tercihleri
                    </h2>
                </div>

                {/* Community Filter */}
                <div className="space-y-4">
                    <h3 className="text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest">
                        Topluluk Filtresi
                    </h3>
                    <div className="flex items-center justify-between gap-4 p-4 bg-surface-50 dark:bg-surface-800 rounded-xl border border-surface-100 dark:border-surface-700">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <Shield className="w-4 h-4 text-mavi-500" />
                                <span className="font-poppins font-medium text-sm text-gray-900 dark:text-white">Sadece Mavililer</span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed max-w-md">
                                Bu ayar açıkken, "mavililer" dışında başka istemcilerden veya botlardan gelen ilgisiz içerikler gizlenir.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-mavi-500 rounded-full text-white shadow-sm shadow-mavi-500/20">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            <span className="text-[10px] font-bold uppercase tracking-wider">Aktif</span>
                        </div>
                    </div>
                </div>

                {/* Cancelled: Removed backup section as it's better placed in profile or ignored for now to keep settings clean, 
                    OR better: Keep it if user explicitly asked for "app settings" consolidation.
                    The user said "admin paneli butonuna şuan basınca uygulama ayarı gibi bir kısım da geliyor... settings içine entegre et".
                    The popup had "Community Filter", "Secret Key Backup" and "Pubkey".
                    I will include Secret Key Backup here as well since it was in the popup.
                 */}

                {/* Secret Key Backup */}
                {sk && (
                    <div className="space-y-4 pt-6 border-t border-surface-100 dark:border-surface-800">
                        <h3 className="text-[10px] font-poppins font-bold text-red-500 uppercase tracking-widest flex items-center gap-2">
                            <ShieldAlert className="w-3 h-3" />
                            Hesap Güvenliği
                        </h3>

                        <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/50 rounded-xl space-y-3">
                            <div className="flex items-start gap-3">
                                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg shrink-0">
                                    <Key className="w-5 h-5 text-red-600 dark:text-red-400" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-red-900 dark:text-red-400 mb-1">Hesabını Yedekle</h4>
                                    <p className="text-xs text-red-800 dark:text-red-300/80 leading-relaxed mb-3">
                                        Şu an tarayıcı hafızasında saklanan bir hesap kullanıyorsun. Tarayıcı verilerin temizlenirse hesabını kaybedersin.
                                        Aşağıdaki "nsec" ile başlayan kodu güvenli bir yere kaydet.
                                    </p>

                                    <div className="relative group">
                                        <div className={`p-3 bg-white dark:bg-surface-950 border border-red-200 dark:border-red-800 rounded-lg text-xs font-mono break-all pr-20 transition-all ${!showSk ? 'blur-sm select-none opacity-50' : 'opacity-100'}`}>
                                            {nsec}
                                        </div>

                                        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-white/50 dark:bg-black/50 backdrop-blur-sm p-1 rounded-md">
                                            <button
                                                onClick={() => setShowSk(!showSk)}
                                                className="p-1.5 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-md transition-colors text-gray-600 dark:text-gray-300"
                                                title={showSk ? "Gizle" : "Göster"}
                                            >
                                                {showSk ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                            <button
                                                onClick={handleCopy}
                                                className="p-1.5 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-md transition-colors text-mavi-600 dark:text-mavi-400"
                                                title="Kopyala"
                                            >
                                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Relay Management */}
            <div className="bg-white dark:bg-surface-900 rounded-2xl p-8 border border-surface-200 dark:border-surface-700 space-y-6">
                <div className="flex items-center gap-3">
                    <Radio className="w-5 h-5 text-mavi-500" />
                    <h2 className="font-poppins font-bold text-xl text-gray-900 dark:text-white">
                        Erişim Noktaları
                    </h2>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 font-merriweather">
                    İçeriklerin hangi sunuculardan çekileceğini ve nereye yayınlanacağını belirle.
                </p>

                {/* Add New Relay */}
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={newRelayUrl}
                        onChange={(e) => setNewRelayUrl(e.target.value)}
                        placeholder="wss://relay.example.com"
                        className="flex-1 px-4 py-3 rounded-xl border-2 border-surface-100 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50 focus:bg-white dark:focus:bg-surface-900 focus:outline-none focus:ring-4 focus:ring-mavi-500/10 focus:border-mavi-500 transition-all font-mono text-sm"
                    />
                    <button
                        onClick={addRelay}
                        className="px-6 py-3 bg-mavi-500 hover:bg-mavi-600 text-white rounded-xl font-poppins font-bold transition-all flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Ekle
                    </button>
                </div>

                {/* Relay List */}
                <div className="space-y-2">
                    {isLoading ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400 font-merriweather">
                            Yükleniyor...
                        </div>
                    ) : relays.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400 font-merriweather">
                            Henüz sunucu eklenmemiş
                        </div>
                    ) : (
                        relays.map((relay, index) => (
                            <motion.div
                                key={relay.url}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="flex items-center gap-4 p-4 rounded-xl bg-surface-50 dark:bg-surface-800/50 border border-surface-100 dark:border-surface-800"
                            >
                                <div className="flex-1 font-mono text-sm text-gray-700 dark:text-gray-300 truncate">
                                    {relay.url}
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => toggleRelayRead(relay.url)}
                                        className={`px-3 py-1 rounded-lg text-xs font-poppins font-bold transition-all ${relay.read
                                            ? 'bg-green-500 text-white'
                                            : 'bg-surface-200 dark:bg-surface-700 text-gray-500'
                                            }`}
                                    >
                                        Oku
                                    </button>
                                    <button
                                        onClick={() => toggleRelayWrite(relay.url)}
                                        className={`px-3 py-1 rounded-lg text-xs font-poppins font-bold transition-all ${relay.write
                                            ? 'bg-blue-500 text-white'
                                            : 'bg-surface-200 dark:bg-surface-700 text-gray-500'
                                            }`}
                                    >
                                        Yaz
                                    </button>
                                    <button
                                        onClick={() => removeRelay(relay.url)}
                                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4 text-red-500" />
                                    </button>
                                </div>
                            </motion.div>
                        ))
                    )}
                </div>

                {/* Save Button */}
                <button
                    onClick={saveRelays}
                    disabled={isSaving}
                    className="w-full py-4 bg-gradient-to-r from-mavi-500 to-mavi-600 hover:from-mavi-600 hover:to-mavi-700 text-white rounded-xl font-poppins font-bold shadow-lg shadow-mavi-500/20 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    {isSaving ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                        <>
                            <Save className="w-5 h-5" />
                            Kaydet
                        </>
                    )}
                </button>
            </div>
        </motion.div>
    );
}
